/**
 * Reporting surface access helpers.
 *
 * Org Agent (`gailAgentEnabled`) unlocks EOD. ReportUser unlocks the Metabase
 * dashboard (and Feedback when combined with GPT). Left-nav Reporting is shown
 * when either applies.
 */

import {GPT_VERSION, PRODUCT_NAME} from "@/enums/profile";

export const REPORTING_DASHBOARD_HREF = "/reporting";
export const REPORTING_EOD_HREF = "/reporting/eod-report";

type OrgAgentProductSource = {
   features?: {gptVersion?: string} | null;
   products?: Array<{name?: string; productOffering?: string}> | null;
};

/**
 * Mirrors `productAccess.gailAgentEnabled` for server-side org payloads:
 * GPT V1 orgs, or any org listing a GailAgent product.
 */
export function orgHasGailAgentEnabled(org: OrgAgentProductSource | null | undefined): boolean {
   if (!org) return false;
   const isOrgGailGptV1 =
      org.features?.gptVersion?.toLowerCase() === GPT_VERSION.V1.toLowerCase();
   if (isOrgGailGptV1) return true;
   return (
      org.products?.some(
         (product) =>
            product.name === PRODUCT_NAME.GailAgent ||
            product.productOffering === PRODUCT_NAME.GailAgent
      ) ?? false
   );
}

export function canAccessEodReport(gailAgentEnabled: boolean): boolean {
   return gailAgentEnabled;
}

export function canAccessReportingDashboard(isReportUser: boolean): boolean {
   return isReportUser;
}

/**
 * Reporting has two independent axes and needs both. The product axis decides which reporting
 * surface exists for the caller at all; `hasReportingRead` is the `report.read` scope from the
 * permission matrix. Taking the scope as a parameter keeps the rule here rather than letting each
 * nav container decide whether to consult it.
 */
export function canSeeReportingNav(
   gailAgentEnabled: boolean,
   isReportUser: boolean,
   hasReportingRead: boolean
): boolean {
   if (!hasReportingRead) return false;
   return canAccessEodReport(gailAgentEnabled) || canAccessReportingDashboard(isReportUser);
}

/**
 * Prefer dashboard when the user has ReportUser; otherwise EOD (org Agent only).
 * Callers should only use this when {@link canSeeReportingNav} is true.
 */
export function getReportingNavHref(
   _gailAgentEnabled: boolean,
   isReportUser: boolean
): typeof REPORTING_DASHBOARD_HREF | typeof REPORTING_EOD_HREF {
   return canAccessReportingDashboard(isReportUser)
      ? REPORTING_DASHBOARD_HREF
      : REPORTING_EOD_HREF;
}
